import { Droplets, Loader2 } from 'lucide-react';
import { assessWater, type Incident } from '@duty/shared';
import { useIncidentStore } from '@/incident/store';
import { Field, TextInput } from '@/components/ui/controls';

/** Water Plan capture + live supply assessment. */
export function WaterPanel({ incident }: { incident: Incident }) {
  const { dispatch, config, osmWaterStatus } = useIncidentStore();
  const plan = incident.waterPlan;
  const a = assessWater(plan, incident.resources, config);
  const isLiveOsm = !incident.locality && plan.nearestHydrantDistanceM != null;

  return (
    <div className="space-y-2.5">
      <div className="grid grid-cols-2 gap-2">
        <Field label="Nearest hydrant (m)">
          <TextInput
            value={plan.nearestHydrantDistanceM != null ? String(plan.nearestHydrantDistanceM) : ''}
            onChange={(v) => {
              const n = v.trim() === '' ? undefined : Number(v);
              dispatch({
                type: 'update-water',
                patch: { nearestHydrantDistanceM: Number.isFinite(n as number) ? (n as number) : undefined },
              });
            }}
            placeholder="e.g. 2000"
            ariaLabel="Nearest hydrant distance in metres"
          />
        </Field>
        <label className="flex items-end gap-2 pb-1.5 text-sm">
          <input
            type="checkbox"
            checked={plan.staticWaterAvailable === true}
            onChange={(e) => dispatch({ type: 'update-water', patch: { staticWaterAvailable: e.target.checked } })}
          />
          Static water available
        </label>
      </div>

      <div
        className={`flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-xs ${
          a.limited ? 'border-high/40 text-high' : 'border-ok/40 text-ok'
        }`}
      >
        <Droplets size={14} />
        {a.limited ? 'Sustained supply looks limited' : 'Supply appears adequate'}
        <span className="ml-auto font-mono text-muted">
          {a.onboardWaterL} L onboard · hydrant{' '}
          {a.hydrantKnown ? (a.hydrantInRange ? 'in range' : 'distant') : 'unknown'}
          {a.bulkWaterAssigned ? ' · bulk ✓' : ''}
        </span>
      </div>

      {osmWaterStatus === 'loading' && (
        <p className="flex items-center gap-1.5 text-[10px] text-muted">
          <Loader2 size={11} className="animate-spin" /> Checking OpenStreetMap for hydrants / static water…
        </p>
      )}
      {osmWaterStatus === 'unavailable' && plan.nearestHydrantDistanceM == null && (
        <p className="text-[10px] text-muted">
          OpenStreetMap lookup unavailable (offline or unreachable) — hydrant/static water unknown; enter manually if known.
        </p>
      )}

      {plan.nearestHydrantDistanceM != null && (
        <p className="text-[10px] text-muted">
          Nearest hydrant ~{plan.nearestHydrantDistanceM} m
          {plan.hydrantConfidence ? ` (${plan.hydrantConfidence} confidence)` : ''} ·{' '}
          {plan.staticWaterNote ? `static: ${plan.staticWaterNote}` : 'no static water nearby'} ·{' '}
          {isLiveOsm ? 'computed from live OpenStreetMap data' : 'computed from seeded map data'}
        </p>
      )}
    </div>
  );
}
